/**
 * Pure helpers for the WagePro-branded admin signup screen (WagePro signup
 * epic, `wagepro_signup_screen.tsx`). No React, no I/O — unit-tested in
 * isolation, matching this codebase's existing `auth_gate_screen.ts`/
 * `apps/tax-web/src/screens/ess_accept_logic.ts` "pure logic" convention.
 *
 * Calls `wageproAuth.signup` (`apps/api/src/trpc/routers/wagepro_auth.ts`).
 */

import {
  classifyAuthError,
  type AuthErrorDisplay,
} from './auth_error_display.js';
import { passwordConfirmationMatches } from './password_confirmation.js';

/** The pre-auth pathname `main.tsx` gates on, mirroring `apps/tax-web/src/App.tsx`'s `ESS_ACCEPT_PATH`. */
export const SIGNUP_PATH = '/signup';

export interface WageProSignupFormValues {
  email: string;
  password: string;
  /** Must equal `password` exactly. Client-side only — never sent to the API. */
  confirm_password: string;
  full_name: string;
  company_name: string;
  accept_tos: boolean;
  accept_privacy: boolean;
}

/**
 * Which fields currently fail client-side validation. A key present (any
 * value, always `true`) means that field is invalid; the screen maps each
 * key to its own translated error copy — this module never renders text.
 */
export type SignupFieldErrors = Partial<
  Record<keyof WageProSignupFormValues, true>
>;

// Deliberately permissive (matches the general shape the server's
// `z.string().email()` accepts) — this is a fast, friendly pre-check, not
// the source of truth. The server's Zod schema is what actually enforces
// validity; a client-side false negative here just means one extra round
// trip, never a security boundary.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Matches `provisionInputSchema`'s `password.min(12)` server-side (`wagepro_signup.ts`). */
export const MIN_PASSWORD_LENGTH = 12;

/**
 * Client-side pre-validation mirroring `provisionInputSchema`
 * (`wagepro_signup.ts`): non-empty trimmed `full_name`/`company_name`, a
 * plausible email shape, a password of at least {@link MIN_PASSWORD_LENGTH}
 * characters, and both consent checkboxes checked (GDPR Article 7 — the
 * server rejects an unchecked/missing checkbox with `z.literal(true)`, same
 * contract as `auth.register`).
 */
export function validateSignupForm(
  values: WageProSignupFormValues,
): SignupFieldErrors {
  const errors: SignupFieldErrors = {};
  if (!EMAIL_RE.test(values.email.trim())) errors.email = true;
  if (values.password.length < MIN_PASSWORD_LENGTH) errors.password = true;
  if (!passwordConfirmationMatches(values.password, values.confirm_password)) {
    errors.confirm_password = true;
  }
  if (values.full_name.trim().length === 0) errors.full_name = true;
  if (values.company_name.trim().length === 0) errors.company_name = true;
  if (!values.accept_tos) errors.accept_tos = true;
  if (!values.accept_privacy) errors.accept_privacy = true;
  return errors;
}

export function hasSignupFieldErrors(errors: SignupFieldErrors): boolean {
  return Object.keys(errors).length > 0;
}

// A caught `wageproAuth.signup` rejection is turned into display copy by
// `classifyAuthError` (`lib/auth/auth_error_display.ts`), shared with the
// login screen. It used to be a `signupErrorMessage` helper here that
// returned `err.message` for anything carrying one — which rendered raw
// `fetch` failures ("Failed to fetch") as form-error copy. See that module.

/**
 * The exact message `wageproAuth.signup` (`apps/api/src/trpc/routers/wagepro_auth.ts`)
 * rejects an already-registered email with. Generic by design — it is the
 * same copy a wrong-password login gets, so the response cannot be used to
 * enumerate which addresses have accounts (`docs/security-rules.md`).
 */
const SIGNUP_DUPLICATE_EMAIL_MESSAGE = 'Invalid credentials';

/**
 * Classify a caught `wageproAuth.signup` rejection for display.
 *
 * Identical to `classifyAuthError`, except for one case: the server's
 * duplicate-email rejection (`BAD_REQUEST` + "Invalid credentials"). Shown
 * verbatim on a signup form that is nonsense — the person just chose a new
 * password and is told their credentials are wrong. It becomes
 * `signup_account_exists`, whose copy points them at sign-in.
 *
 * The copy states plainly that the account exists (a product decision). That
 * reveals nothing an attacker can't already read off the raw response: the
 * duplicate-email rejection is the only `BAD_REQUEST` "Invalid credentials"
 * this handler sends. The `BAD_REQUEST` check matters: the same handler sends
 * "Invalid credentials" as `FORBIDDEN` for a failed Turnstile bot check,
 * which must NOT be told to sign in instead.
 */
export function classifySignupError(err: unknown): AuthErrorDisplay {
  const display = classifyAuthError(err);
  if (
    display.kind === 'server' &&
    display.message === SIGNUP_DUPLICATE_EMAIL_MESSAGE &&
    readTrpcCode(err) === 'BAD_REQUEST'
  ) {
    return { kind: 'signup_account_exists' };
  }
  return display;
}

/** The tRPC error code (`data.code`) on a server rejection, if present. */
function readTrpcCode(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null || !('data' in err)) {
    return undefined;
  }
  const data = (err as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null || !('code' in data)) {
    return undefined;
  }
  const code = (data as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}
